import type { AcquisitionChannel, SubscriptionStatus } from "@/domains/subscription/subscription.types";
import type { SubscriptionLocale } from "@/locales/fr/subscription";

const acquisitionChannel: Record<AcquisitionChannel, string> = {
  DIGITAL_LEAD: "Digital lead",
  FULL_DIGITAL_SUBSCRIPTION: "Full digital",
  DIRECT_ACQUISITION: "Direct acquisition",
  EXTERNAL_VENDOR: "External vendor",
  LANDLORD: "Landlord",
};

const subscriptionStatus: Record<SubscriptionStatus, string> = {
  RUNNING: "Running",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

const subscriptionLocale: SubscriptionLocale = {
  subscription: {
    create: {
      cta: "Create a subscription",
      confirmation: {
        title: "Create a subscription",
        message: "Do you want to create a new subscription for this site?",
        replaceExistingMessage:
          "A subscription is already in progress. The new subscription will close the existing one and replace it. Do you want to continue?",
        acceptLabel: "Create subscription",
        rejectLabel: "Cancel",
      },
    },
    emptyState: {
      title: "No subscription",
      description: "No subscription has been created for this site yet.",
    },
    errorState: {
      title: "Error occurred while loading the subscription",
      description: "Please try again later.",
    },
    header: {
      title: "Subscription #{id}",
      startedOn: "Started on {date}",
      closedOn: "Closed on {date}",
      delayedClosureLifted: "delayed closure of {date} lifted",
      subscriptionStatus,
    },
    pipelineTimeline: {
      status: {
        initial: "Action required",
        done: "Completed",
        failed: "Failed",
        pending: "Pending",
        notRequired: "Not required",
        inProgress: "In progress",
        finalizationRequired: "Finalization required",
      },
      step: {
        prospection: "Prospecting",
        confirmation: "Confirmation",
        installation: "Installation",
      },
    },
    activeCard: {
      takeAppointmentCta: "Book an appointment",
      actionsMenuCta: "Show other actions",
      showDetailsCta: "Show details",
      hideDetailsCta: "Hide details",
      confirmEligibilityCta: "Confirm eligibility",
      reOpenSubscriptionCta: "Re-open subscription",
      viewHistoryCta: "Subscription history",
      failureLabel: "Failed",
      delayedClosure: {
        label: "Scheduled closure",
        scheduledOn: "Scheduled for {date}",
      },
      pastFailures: {
        message:
          "No past failed subscription | One past subscription ended in failure on this site. | {count} past subscriptions ended in failure on this site.",
        cta: "View history",
      },
      prospection: {
        title: "Prospecting Phase",
        partnerLabel: "Partner",
        acquisitionChannelLabel: "Acquisition channel",
        acquisitionChannel,
      },
      eligibility: {
        title: "Eligibility Confirmation",
      },
      tags: {
        title: "Tags",
        empty: "No tag",
        count: "{count} tag(s)",
        addPlaceholder: "Add a tag",
        filterPlaceholder: "Search for a tag",
        noAvailableTags: "No tag available",
        noSearchResult: "No matching tag",
        availableTagsError: "Tags unavailable",
        removeCta: "Remove tag",
      },
      installation: {
        title: "Installation",
        awaitingAppointment: "The subscriber is waiting for the installation appointment to be scheduled.",
        appointmentCanceled: "Previous intervention canceled, an appointment must be scheduled for this site",
        appointmentCompromised: "This appointment is compromised, a new appointment must be scheduled for this site",
        dateAndTimeLabel: "Date & time",
        technicianLabel: "Technician",
        technicianUnknown: "Unknown",
        newInterventionRequired: "A finalization appointment is required",
      },
    },
    history: {
      title: "Subscription history",
      filter: {
        label: "Status",
        all: "All",
        subscriptionStatus,
      },
      resultCount: "No subscription | {count} subscription | {count} subscriptions",
      truncationNotice: "The {shown} most recent subscriptions out of {total}.",
      currentBadge: "Current subscription",
      startedOn: "Started on {date}",
      closedOn: "Closed on {date}",
      noFailure: "No failure recorded",
      unknownOperator: "Unknown operator",
      details: {
        subscriberLabel: "Subscriber",
        pipelineLabel: "Pipeline",
        reasonCodeLabel: "Failure code",
      },
      showDetailsCta: "Show details",
      hideDetailsCta: "Hide details",
      errorState: {
        title: "Error occurred while loading the history",
        description: "Please try again later.",
      },
      emptyState: {
        title: "No subscription",
        description: "No subscription matches this filter.",
      },
    },
  },
};

export default subscriptionLocale;
